import { useEffect, useRef } from 'react';

// Performance monitoring hook
export const usePerformanceMonitor = (componentName) => {
  const renderStartTime = useRef(performance.now());
  const mountTime = useRef(null);
  
  useEffect(() => {
    // Component mounted
    mountTime.current = performance.now();
    const mountDuration = mountTime.current - renderStartTime.current;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PERF] ${componentName} mounted in ${mountDuration.toFixed(2)}ms`);
    }
    
    return () => {
      // Component unmounted
      const unmountTime = performance.now();
      const totalLifetime = unmountTime - mountTime.current;
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[PERF] ${componentName} lifetime: ${totalLifetime.toFixed(2)}ms`);
      }
    };
  }, [componentName]);
  
  // Update render start time on each render
  renderStartTime.current = performance.now();
};

// Memory usage monitoring
export const useMemoryMonitor = () => {
  useEffect(() => {
    const interval = setInterval(() => {
      if (performance.memory && process.env.NODE_ENV === 'development') {
        const { usedJSHeapSize, totalJSHeapSize } = performance.memory;
        const usedMB = (usedJSHeapSize / 1024 / 1024).toFixed(2);
        const totalMB = (totalJSHeapSize / 1024 / 1024).toFixed(2);
        console.log(`[MEMORY] Usage: ${usedMB}MB / ${totalMB}MB`);
      }
    }, 30000); // Log every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
};

// Core Web Vitals monitoring
export const useWebVitals = () => {
  useEffect(() => {
    // Only run in production or when explicitly enabled
    if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_MONITOR_VITALS === 'true') {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      }).catch(err => {
        console.log('Web Vitals not available:', err);
      });
    }
  }, []);
};